import { Controller } from "@hotwired/stimulus";

// Drag & drop file picker. Used by both the avatar field (image only) and
// each document slot (PDF + image). Validation is mirrored server-side in
// OnboardingController#valid_image? / valid_document?.
//
//   data-controller="dropzone"
//   data-dropzone-accept-value="image/jpeg,image/png,..."   // CSV mime list
//   data-dropzone-max-size-value="10485760"                  // bytes
//   data-dropzone-mode-value="image"                         // "image" → preview thumbnail
//
// Targets : zone, input, placeholder, preview, previewImg, filename, error
export default class extends Controller {
  static targets = ["zone", "input", "placeholder", "preview", "previewImg", "filename", "error"];
  static values = {
    accept:  { type: String, default: "" },
    maxSize: { type: Number, default: 10 * 1024 * 1024 },
    mode:    { type: String, default: "doc" }, // "image" | "doc"
  };

  connect() {
    this._dragCounter = 0;
  }

  // ── Drag events ─────────────────────────────────────────────────────

  dragover(event) {
    // dragover se declenche en continu : on se contente d'autoriser le drop et
    // d'afficher la surbrillance (pas de compteur, qui restait bloque > 0 quand
    // l'utilisateur survolait puis ressortait sans deposer).
    event.preventDefault();
    event.stopPropagation();
    this.zoneTarget.classList.add("ob-dropzone--hover");
  }

  dragleave(event) {
    event.preventDefault();
    event.stopPropagation();
    // On ne retire la surbrillance que si on quitte reellement la zone (et non
    // en passant au-dessus d'un enfant : relatedTarget reste dans la zone).
    if (!this.zoneTarget.contains(event.relatedTarget)) {
      this.zoneTarget.classList.remove("ob-dropzone--hover");
    }
  }

  drop(event) {
    event.preventDefault();
    event.stopPropagation();
    this._dragCounter = 0;
    this.zoneTarget.classList.remove("ob-dropzone--hover");
    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!this._validate(file)) return;
    // Sync into the underlying <input type=file> via DataTransfer so the
    // form submit picks it up exactly as a normal selection would.
    const dt = new DataTransfer();
    dt.items.add(file);
    this.inputTarget.files = dt.files;
    this._showPreview(file);
  }

  // ── Native input change ─────────────────────────────────────────────

  fileSelected(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!this._validate(file)) {
      event.target.value = "";
      return;
    }
    this._showPreview(file);
  }

  clear(event) {
    event?.preventDefault();
    this.inputTarget.value = "";
    if (this.hasPreviewTarget) this.previewTarget.hidden = true;
    if (this.hasPlaceholderTarget) this.placeholderTarget.hidden = false;
    this._hideError();
  }

  // ── Internals ───────────────────────────────────────────────────────

  _validate(file) {
    this._hideError();

    if (file.size > this.maxSizeValue) {
      this._showError(`Fichier trop lourd (${this._formatSize(file.size)}). Limite : ${this._formatSize(this.maxSizeValue)}.`);
      return false;
    }

    const accepts = this.acceptValue
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);

    if (accepts.length > 0) {
      const mime = (file.type || "").toLowerCase();
      const ok = accepts.some((rule) => {
        if (rule.endsWith("/*")) return mime.startsWith(rule.slice(0, -1));
        return mime === rule;
      });
      if (!ok) {
        const human = this.modeValue === "image"
          ? "Format non supporté. Accepté : JPG, PNG, HEIC ou WEBP."
          : "Format non supporté. Accepté : PDF ou image.";
        this._showError(human);
        return false;
      }
    }

    return true;
  }

  _showPreview(file) {
    if (this.hasPlaceholderTarget) this.placeholderTarget.hidden = true;
    if (!this.hasPreviewTarget) return;
    this.previewTarget.hidden = false;
    if (this.hasFilenameTarget) {
      this.filenameTarget.textContent = file.name;
    }
    if (this.modeValue === "image" && this.hasPreviewImgTarget) {
      const url = URL.createObjectURL(file);
      this.previewImgTarget.src = url;
      this.previewImgTarget.onload = () => URL.revokeObjectURL(url);
    }
  }

  _showError(msg) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = msg;
    this.errorTarget.hidden = false;
  }

  _hideError() {
    if (!this.hasErrorTarget) return;
    this.errorTarget.hidden = true;
    this.errorTarget.textContent = "";
  }

  _formatSize(bytes) {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  }
}
